import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { voiceApi } from '../../api/voice'
import { RefusedError } from '../../api/client'
import { MailboxWarning } from '../../components/MailboxWarning'
import { useSpoke } from '../../hooks/useSpoke'
import { useSession } from '../../hooks/useSession'
import { refuseOnMailbox } from '../../lib/files'
import { formatDate } from '../../lib/format'
import type { MailExportFormat, MailboxView, UploadSummary } from '../../types/voice'

// Step 1 of the Digital Landscape: your own mailbox. Two ways in — consent (Microsoft 365) or an
// export file — with the same promise either way: sent items only, your own words only, nothing
// ever sent. The page states what will be read before anything is read, shows what an upload
// produced before anything is learned, and the grant can be withdrawn here, which removes
// everything derived from it.
export function Mailbox() {
  const spoke = useSpoke()
  const { user } = useSession()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const manage = params.get('manage') === '1'
  const mailbox = useQuery({ queryKey: ['mailbox', spoke], queryFn: () => voiceApi.mailbox(spoke) })
  const formats = useQuery({ queryKey: ['mailbox-formats', spoke], queryFn: () => voiceApi.formats(spoke), staleTime: Infinity })
  const [address, setAddress] = useState(user?.email.includes('@') ? user.email : '')
  const [file, setFile] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [summary, setSummary] = useState<UploadSummary | null>(null)
  const [wrongFile, setWrongFile] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const resumed = useRef(false)

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['mailbox', spoke] })
    void qc.invalidateQueries({ queryKey: ['landscape', spoke] })
    void qc.invalidateQueries({ queryKey: ['voice-profile', spoke] })
  }, [qc, spoke])

  const connect = useMutation({
    mutationFn: () => voiceApi.connect(spoke, 'm365-mailbox'),
    onSuccess: (r) => window.location.assign(r.authorizeUrl),
  })
  // 'superhuman' when the person chose the Superhuman path: same export, the source is recorded as Superhuman.
  const [via, setVia] = useState('')
  const upload = useMutation({
    mutationFn: () => voiceApi.upload(spoke, file!, address, user?.displayName ?? null, setProgress, via || undefined),
    onSuccess: (s) => {
      setSummary(s)
      setFile(null)
      setProgress(null)
      invalidate()
      navigate(`/jobs/${encodeURIComponent(s.jobId)}`)
    },
    onError: () => setProgress(null),
  })
  const revoke = useMutation({ mutationFn: () => voiceApi.revoke(spoke), onSuccess: () => { setSummary(null); invalidate() } })
  const build = useMutation({ mutationFn: () => voiceApi.build(spoke), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })

  useEffect(() => { if (!address && user?.email.includes('@')) setAddress(user.email) }, [user, address])

  // Once a mailbox is connected, its read console is the screen: a read in progress, a landed read
  // (clean or with gaps) all open there. A read that stopped is resumed, once, on arrival. Only
  // ?manage=1 (add an export, disconnect) stays here.
  useEffect(() => {
    const v = mailbox.data
    if (!v || manage) return
    const target = v.activeBuild ?? v.lastBuild
    if ((v.readState === 'running' || v.readState === 'complete' || v.readState === 'degraded') && target) navigate(`/jobs/${encodeURIComponent(target.id)}`, { replace: true })
    else if (v.readState === 'stopped' && !resumed.current && !build.isPending) { resumed.current = true; build.mutate() }
  }, [mailbox.data, manage, navigate, build])

  const accept = (formats.data ?? []).flatMap((f) => f.extensions).join(',')
  // A contact list offered here is refused by naming the screen that reads it, not by naming the
  // formats this one wants (REQ-063-AC3).
  const pick = (f: File | undefined) => {
    if (!f) return
    const elsewhere = refuseOnMailbox(f.name)
    setWrongFile(elsewhere)
    if (!elsewhere) setFile(f)
  }
  const onDrop = (e: DragEvent) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]) }
  const refusalOf = (err: unknown) => (err instanceof RefusedError ? err.refusal.message : err instanceof Error ? err.message : 'Unavailable')
  const formatFor = (name: string) => (formats.data ?? []).find((f) => f.extensions.some((e) => name.toLowerCase().endsWith(e)))
  const size = (n: number) => (n > 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`)

  return (
    <section className="page mailbox">
      <p className="kicker"><Link to="/landscape">Digital Landscape</Link> · step 1 of 5{manage && mailbox.data?.lastBuild ? <> · <Link to={`/jobs/${encodeURIComponent(mailbox.data.lastBuild.id)}`}>back to the read</Link></> : ''}</p>
      <h1>Your mailbox</h1>
      <p className="lede">Your sent mail is where your voice lives. Connect it once; Scout learns how you write, per audience and intent, from the words you typed and nothing else.</p>

      {/* The credential stopped opening: say so here, where the person is, rather than showing the
          last read's numbers as though they were today's (REQ-064-AC3). */}
      <MailboxWarning lastReadAt={mailbox.data?.read?.completedAt ?? null} action={false} />
      {wrongFile && (
        <div className="refusal" role="status">
          <strong>That is not a mail export</strong>
          <p>{wrongFile}</p>
          <p><Link className="button" to="/onboarding">Open Onboard Contacts</Link></p>
        </div>
      )}

      {mailbox.isPending && <p className="muted">Loading…</p>}
      {mailbox.isError && (
        <div className="refusal" role="status">
          <strong>{mailbox.error instanceof RefusedError ? mailbox.error.refusal.reason : 'Unavailable'}</strong>
          <p>{mailbox.error.message}</p>
        </div>
      )}

      {mailbox.isSuccess && mailbox.data.connected && mailbox.data.connection && (
        <div className={`card connected-card ${mailbox.data.readState}`}>
          <div className="connected-head">
            <StateMark state={mailbox.data.readState} />
            <div>
              <p className="kicker">{readKicker(mailbox.data)}</p>
              <h2>{mailbox.data.connection.address}</h2>
              <p className="muted">{mailbox.data.connection.catalogType === 'm365-mailbox' ? 'Microsoft 365, with your consent. Scout reads new sent mail on every build.' : mailbox.data.connection.catalogType === 'superhuman-mailbox' ? 'Superhuman, from the export of the account beneath it. Upload a newer export any time to add to it.' : 'From an export file. Upload a newer one any time to add to it.'}</p>
            </div>
          </div>
          {summary && (
            <div className="upload-summary">
              <Stat n={summary.parsed} label="messages in the file" />
              <Stat n={summary.fromYou} label={`sent from ${summary.connection.address}`} />
              <Stat n={summary.kept} label="kept to learn from" accent />
              {summary.toYou > 0 && <Stat n={summary.toYou} label="written to you" />}
              <div className="stat">
                <strong>{summary.earliest ? formatDate(summary.earliest) : '—'}</strong>
                <span className="muted">to {summary.latest ? formatDate(summary.latest) : '—'}</span>
              </div>
            </div>
          )}
          {mailbox.data.read && (
            <dl className="facts timeline">
              <dt>Last read</dt><dd>{mailbox.data.read.completedAt ? formatDate(mailbox.data.read.completedAt) : '—'}{mailbox.data.readState === 'degraded' ? ', with gaps' : ''}</dd>
              <dt>Read</dt><dd>{mailbox.data.read.read.toLocaleString()}{mailbox.data.read.windowExpected != null ? ` of the ${mailbox.data.read.windowExpected.toLocaleString()} the mailbox reports` : ' sent messages'}{mailbox.data.read.windowStart ? ` since ${formatDate(mailbox.data.read.windowStart)}` : ''} · {mailbox.data.read.kept.toLocaleString()} of yours kept to learn from</dd>
              <dt>Covering</dt><dd>{mailbox.data.read.earliest ? formatDate(mailbox.data.read.earliest) : '—'} to {mailbox.data.read.latest ? formatDate(mailbox.data.read.latest) : '—'}</dd>
              {mailbox.data.read.sourceSentTotal != null && <><dt>Mailbox holds</dt><dd>{mailbox.data.read.sourceSentTotal.toLocaleString()} messages all-time across the folders Scout reads (Sent Items, Deleted Items, Archive and the Inbox){mailbox.data.read.windowExpected != null && mailbox.data.read.sourceSentTotal > mailbox.data.read.windowExpected ? `; this read covered the window above, not all of them` : ''}</dd></>}
              <dt>Connected</dt><dd>{formatDate(mailbox.data.connection.connectedAt)}</dd>
            </dl>
          )}
          {!mailbox.data.read && (
            <dl className="facts timeline">
              <dt>Connected</dt><dd>{formatDate(mailbox.data.connection.connectedAt)}</dd>
              <dt>Read</dt><dd>{mailbox.data.readState === 'stopped' ? 'stopped before it finished; resuming' : mailbox.data.readState === 'cancelled' ? 'cancelled' : 'not yet'}</dd>
            </dl>
          )}
          {mailbox.data.readState === 'degraded' && mailbox.data.read && (
            <div className="gaps">
              <strong>What the last read did not do</strong>
              <ul>{mailbox.data.read.gaps.map((g) => <li key={g}>{g}</li>)}</ul>
              <p className="muted small">Each of these is one thing the last read could not do; everything else it landed stands, and the next build tries them again{mailbox.data.lastBuild ? <> — <Link to={`/jobs/${encodeURIComponent(mailbox.data.lastBuild.id)}`}>open the last read</Link></> : ''}.</p>
            </div>
          )}
          {mailbox.data.readState === 'complete' && mailbox.data.lastBuild && <p className="muted small"><Link to={`/jobs/${encodeURIComponent(mailbox.data.lastBuild.id)}`}>Open the last read</Link></p>}
          <div className="actions">
            <button className="button primary" onClick={() => build.mutate()} disabled={build.isPending}>{build.isPending ? 'Starting…' : mailbox.data.connection.lastSyncAt ? 'Read it again' : 'Read my mailbox'}</button>
            <button className="button" onClick={() => inputRef.current?.click()}>Add another export</button>
            <button className="button danger" onClick={() => revoke.mutate()} disabled={revoke.isPending}>Disconnect and delete everything learned</button>
          </div>
          {build.isError && <p className="error">{refusalOf(build.error)}</p>}
          {revoke.isError && <p className="error">{refusalOf(revoke.error)}</p>}
          <input ref={inputRef} type="file" accept={accept} hidden onChange={(e) => { pick(e.target.files?.[0]); e.currentTarget.value = '' }} />
          {file && <UploadPanel file={file} address={address} setAddress={setAddress} formatFor={formatFor} size={size} progress={progress} onGo={() => upload.mutate()} onClear={() => setFile(null)} busy={upload.isPending} error={upload.isError ? refusalOf(upload.error) : null} />}
        </div>
      )}

      {mailbox.isSuccess && !mailbox.data.connected && (
        <>
          <div className="paths">
            <div className="card path-card recommended">
              <div className="path-head">
                <span className="path-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 7l10 7 10-7" /></svg></span>
                <div>
                  <p className="kicker">Recommended</p>
                  <h2>Connect Microsoft 365</h2>
                </div>
              </div>
              <p>One consent screen from Microsoft. Scout asks to <strong>read</strong> your mail and keeps the grant current on its own, so every build sees your latest sent items.</p>
              <ul className="promises">
                <li><Dot /> Reads your <strong>Sent Items</strong> only</li>
                <li><Dot /> Keeps only the words <strong>you</strong> typed, never quoted replies or signatures</li>
                <li><Dot /> <strong>Never sends</strong> anything, and cannot</li>
                <li><Dot /> Withdraw the grant here at any time; everything learned is deleted with it</li>
              </ul>
              <button className="button primary" onClick={() => connect.mutate()} disabled={connect.isPending}>{connect.isPending ? 'Opening Microsoft…' : 'Connect my mailbox'}</button>
              {connect.isError && (
                <div className="refusal">
                  <strong>{connect.error instanceof RefusedError ? connect.error.refusal.reason : 'Unavailable'}</strong>
                  <p>{refusalOf(connect.error)}</p>
                </div>
              )}
            </div>

            <div className="card path-card">
              <div className="path-head">
                <span className="path-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M12 3v12M7 10l5 5 5-5M4 17v3h16v-3" /></svg></span>
                <div>
                  <p className="kicker">Or</p>
                  <h2>Upload an export</h2>
                </div>
              </div>
              <p>Export your mailbox from Outlook, Gmail, Thunderbird or Apple Mail and drop the file here. Same rules: only messages from your address, only your own words. The file itself is discarded once read.</p>
              <div
                className={`dropzone${dragging ? ' over' : ''}${file ? ' has-file' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click() }}
                aria-label="Choose or drop an export file"
              >
                <input ref={inputRef} type="file" accept={accept} hidden onChange={(e) => { pick(e.target.files?.[0]); e.currentTarget.value = '' }} />
                {file ? (
                  <>
                    <strong>{file.name}</strong>
                    <span className="muted">{size(file.size)} · {formatFor(file.name)?.name ?? 'unknown format'}</span>
                  </>
                ) : (
                  <>
                    <strong>Drop your export here</strong>
                    <span className="muted">or click to choose a file</span>
                  </>
                )}
              </div>
              <ul className="formats">
                {(formats.data ?? []).map((f) => (
                  <li key={f.id} title={f.hint}>
                    <span className="format-ext">{f.extensions[0]}</span>
                    <span>{f.name}</span>
                  </li>
                ))}
              </ul>
              {file && <UploadPanel file={file} address={address} setAddress={setAddress} formatFor={formatFor} size={size} progress={progress} onGo={() => upload.mutate()} onClear={() => setFile(null)} busy={upload.isPending} error={upload.isError ? refusalOf(upload.error) : null} />}
            </div>

            <div className={`card path-card${via === 'superhuman' ? ' recommended' : ''}`}>
              <div className="path-head">
                <span className="path-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M13 2L4 14h7l-1 8 9-12h-7z" /></svg></span>
                <div>
                  <p className="kicker">Or</p>
                  <h2>Superhuman</h2>
                </div>
              </div>
              <p>Superhuman is a client over your Gmail or Microsoft 365 and has no export of its own. Export the account beneath it and upload that; Scout records the source as Superhuman and reads it by the same rules.</p>
              <ul className="promises">
                <li><Dot /> On Gmail: Google Takeout → Mail → <strong>.mbox</strong></li>
                <li><Dot /> On Microsoft 365: Outlook → Open &amp; Export → <strong>.pst</strong></li>
                <li><Dot /> Only messages from your address, only your own words</li>
              </ul>
              <button className={`button ${via === 'superhuman' ? 'primary' : ''}`} onClick={() => { setVia('superhuman'); inputRef.current?.click() }}>{via === 'superhuman' ? 'Choose the export file…' : 'Upload my Superhuman export'}</button>
              {via === 'superhuman' && <p className="muted small">Pick the .mbox or .pst; the upload panel above takes it from there.</p>}
            </div>
          </div>

          <div className="card how-card">
            <h2>How to export</h2>
            <div className="how-grid">
              {(formats.data ?? []).map((f) => (
                <div key={f.id}>
                  <strong>{f.name}</strong>
                  <p className="muted">{f.hint}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  )
}

function readKicker(v: MailboxView): string {
  switch (v.readState) {
    case 'complete': return 'Read complete'
    case 'degraded': return 'Read complete, with gaps'
    case 'stopped': return 'Last read stopped'
    case 'cancelled': return 'Last read cancelled'
    case 'running': return 'Reading now'
    default: return 'Connected, not read yet'
  }
}

function StateMark({ state }: { state: MailboxView['readState'] }) {
  if (state === 'complete') return <span className="check-big" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 12l4 4L19 6" /></svg></span>
  if (state === 'degraded') return <span className="check-big warn" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M12 6v7" /><circle cx="12" cy="17" r="1" fill="#fff" /></svg></span>
  if (state === 'stopped' || state === 'cancelled') return <span className="check-big stop" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M7 7l10 10M17 7L7 17" /></svg></span>
  return <span className="check-big idle" aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="6" /></svg></span>
}

function Dot() {
  return <span className="promise-dot" aria-hidden="true" />
}

function Stat({ n, label, accent }: { n: number; label: string; accent?: boolean }) {
  return (
    <div className={`stat${accent ? ' accent' : ''}`}>
      <strong>{n.toLocaleString()}</strong>
      <span className="muted">{label}</span>
    </div>
  )
}

function UploadPanel({ file, address, setAddress, formatFor, size, progress, onGo, onClear, busy, error }: {
  file: File; address: string; setAddress: (v: string) => void; formatFor: (n: string) => MailExportFormat | undefined; size: (n: number) => string
  progress: number | null; onGo: () => void; onClear: () => void; busy: boolean; error: string | null
}) {
  const fmt = formatFor(file.name)
  return (
    <div className="upload-panel">
      <div className="upload-file">
        <strong>{file.name}</strong>
        <span className="muted">{size(file.size)} · {fmt?.name ?? 'unknown format'}</span>
        <button className="link" onClick={onClear} disabled={busy}>Choose a different file</button>
      </div>
      <label>
        The address you send from
        <input type="email" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="you@yourfirm.com" required />
      </label>
      <p className="muted small">Only messages <em>from</em> this address are kept. Everything else in the file, including mail you received, is discarded.</p>
      {progress !== null && (
        <div className="progress" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100}>
          <div className="progress-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
          <span className="muted small">{progress < 1 ? `Uploading · ${Math.round(progress * 100)}%` : 'Reading the file…'}</span>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      <div className="actions">
        <button className="button primary" onClick={onGo} disabled={!fmt || !address.includes('@') || busy}>{busy ? 'Working…' : 'Read this export'}</button>
      </div>
    </div>
  )
}
